import React, { useState, useEffect } from "react";
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  KeyRound,
  Loader2,
  Mail,
} from "lucide-react";
import Header from "../components/HomePage/header";
import Footer from "../components/HomePage/footer";

const formatTimer = (seconds) => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}:${remainingSeconds < 10 ? `0${remainingSeconds}` : remainingSeconds}`;
};

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState(null);
  const [messageType, setMessageType] = useState(""); // 'success' or 'error'
  const [loading, setLoading] = useState(false); // Track loading state
  const [timer, setTimer] = useState(null); // For the 5-minute countdown
  const [retryTimer, setRetryTimer] = useState(0); // For the 30-second button disable countdown

  // Countdown for 5-minute reset link validity
  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(interval);
    }
    if (timer === 0 && messageType === "success") {
      setMessage("The reset link has expired. Please try again.");
      setMessageType("error");
    }
  }, [timer, messageType]);

  // Countdown for 30-second button disable
  useEffect(() => {
    if (retryTimer > 0) {
      const interval = setInterval(() => {
        setRetryTimer((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [retryTimer]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setRetryTimer(30); // Disable button for 30 seconds

    try {
      const response = await fetch("/api/forgot-password", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.error === "User not found") {
          setMessage("Invalid username. Please check your email.");
        } else {
          setMessage("Failed to send reset email. Please try again.");
        }
        setMessageType("error");
      } else {
        setMessage(
          "Password reset email sent successfully. Please check your email."
        );
        setMessageType("success");
        setTimer(300); // Start the 5-minute countdown (300 seconds)
      }
    } catch (err) {
      setMessage("An error occurred. Please try again later.");
      setMessageType("error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 text-gray-700">
      <Header />
      <main className="px-4 py-10 sm:px-6 lg:px-8">
        <section className="mx-auto grid w-full max-w-6xl gap-8 lg:min-h-[640px] lg:grid-cols-[0.9fr,1.1fr] lg:items-center">
          <div className="max-w-xl">
            <h1 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
              Reset your password
            </h1>
            <p className="mt-3 text-sm leading-6 text-gray-500 sm:text-base">
              Enter the email linked to your PrepNexis account and we will send
              a secure reset link.
            </p>
            <div className="mt-6 border-t border-slate-200 pt-5">
              <p className="text-sm font-semibold text-gray-900">
                The reset link stays active for 5 minutes.
              </p>
              <p className="mt-1 text-sm text-gray-500">
                Check your inbox after submitting the request.
              </p>
            </div>
          </div>

          <div className="rounded-md border border-slate-200 bg-white p-5 shadow-sm sm:p-6 lg:p-8">
            <div className="mb-6 flex items-start gap-3">
              <span className="grid h-11 w-11 shrink-0 place-items-center rounded-md bg-purple-50 text-purple-700">
                <KeyRound className="h-5 w-5" aria-hidden="true" />
              </span>
              <div>
                <p className="text-sm font-semibold uppercase text-purple-700">
                  Forgot password
                </p>
                <h2 className="mt-1 text-xl font-extrabold text-gray-900 sm:text-2xl">
                  Send reset email
                </h2>
              </div>
            </div>

            {message && (
              <div
                className={`mb-5 flex items-start gap-3 rounded-md border px-4 py-3 text-sm ${
                  messageType === "success"
                    ? "border-green-200 bg-green-50 text-green-800"
                    : "border-red-200 bg-red-50 text-red-800"
                }`}
              >
                {messageType === "success" ? (
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                ) : (
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                )}
                <div>
                  <p className="font-semibold">{message}</p>
                  {messageType === "success" && timer > 0 && (
                    <p className="mt-1 text-sm">
                      Link expires in{" "}
                      <span className="font-bold">{formatTimer(timer)}</span>
                    </p>
                  )}
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label
                  htmlFor="reset-email"
                  className="block text-xs font-semibold uppercase text-gray-500"
                >
                  Email address
                </label>
                <div className="relative mt-2">
                  <Mail
                    className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                    aria-hidden="true"
                  />
                  <input
                    id="reset-email"
                    type="email"
                    placeholder="name@example.com"
                    className="w-full rounded-md border border-slate-200 bg-white px-3 py-3 pl-10 text-sm text-gray-800 shadow-sm outline-none transition placeholder:text-gray-400 focus:border-purple-400 focus:ring-2 focus:ring-purple-100"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
              </div>

              <button
                type="submit"
                className={`inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-md px-4 py-2.5 text-sm font-bold shadow-sm transition focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                  retryTimer > 0
                    ? "cursor-not-allowed bg-slate-200 text-slate-500 focus:ring-slate-300"
                    : "bg-violet-600 text-white hover:bg-violet-700 focus:ring-violet-500"
                }`}
                disabled={loading || retryTimer > 0}
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                    Sending
                  </>
                ) : retryTimer > 0 ? (
                  `Retry in ${retryTimer}s`
                ) : (
                  <>
                    <Mail className="h-4 w-4" aria-hidden="true" />
                    Send reset link
                  </>
                )}
              </button>
            </form>

            <div className="mt-6 flex items-center justify-between gap-3 border-t border-slate-200 pt-5">
              <a
                href="/login"
                className="inline-flex items-center gap-2 text-sm font-semibold text-purple-700 transition hover:text-purple-900"
              >
                <ArrowLeft className="h-4 w-4" aria-hidden="true" />
                Back to login
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default ForgotPassword;
